import { lazy, Suspense, useCallback, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

const EmojiPicker = lazy(() => import('emoji-picker-react'));

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

export type tReactionPickerProps = {
  onSelect: (emoji: string) => void;
  currentEmoji?: string;
};

export default function ReactionPicker(props: tReactionPickerProps) {
  const { onSelect, currentEmoji } = props;
  const [showFullPicker, setShowFullPicker] = useState(false);
  const { t } = useTranslation('chat/chat');

  const handleQuickReaction = useCallback(
    (emoji: string) => {
      if (emoji === currentEmoji) {
        onSelect('');
      } else {
        onSelect(emoji);
      }
    },
    [currentEmoji, onSelect],
  );

  const handleFullPickerSelect = useCallback(
    (emojiData: { emoji: string }) => {
      onSelect(emojiData.emoji);
    },
    [onSelect],
  );

  if (showFullPicker) {
    return (
      <div className="bg-base-100 rounded-lg shadow-xl border border-base-300">
        <Suspense
          fallback={
            <div className="w-[350px] h-[400px] flex items-center justify-center">
              <span className="loading loading-spinner loading-sm" />
            </div>
          }
        >
          <EmojiPicker
            onEmojiClick={handleFullPickerSelect}
            width={350}
            height={400}
            searchPlaceholder={t('reactions.searchEmoji')}
            previewConfig={{ showPreview: false }}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1 bg-base-100 rounded-full shadow-xl border border-base-200 px-2 py-1.5"
      role="toolbar"
      aria-label={t('reactions.quickReactions')}
    >
      {QUICK_REACTIONS.map((emoji) => (
        <button
          key={emoji}
          className={classnames(
            'w-9 h-9 flex items-center justify-center rounded-full text-2xl',
            'hover:bg-base-200 transition-colors',
            { 'bg-primary/10': emoji === currentEmoji },
          )}
          onClick={() => handleQuickReaction(emoji)}
          role="radio"
          aria-checked={emoji === currentEmoji}
          aria-label={emoji}
        >
          {emoji}
        </button>
      ))}
      <button
        className="w-9 h-9 flex items-center justify-center rounded-full text-3xl hover:bg-base-200 transition-colors text-base-content/60"
        onClick={() => setShowFullPicker(true)}
        aria-label={t('reactions.moreEmojis')}
      >
        +
      </button>
    </div>
  );
}
